import { api } from "@/lib/api";

export const CHUNK_SIZE = 8;
const MAX_CHUNK_BYTES = 20 * 1024 * 1024;
const MAX_ATTEMPTS = 3;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Uploads files to an album in chunks so very large selections do not hit
 * request size limits. Each chunk is retried on transient gateway/storage
 * failures; a chunk that keeps failing is reported instead of aborting the rest.
 * Returns { uploaded, errors, duplicates }.
 */
export async function uploadInChunks({
  files,
  eventId,
  albumId,
  watermark = false,
  downloadEnabled = true,
  published = true,
  photographer,
  allowSimilar = false,
  signal,
  onProgress,
}) {
  const chunks = [];
  const tagged = files.map((file) => ({
    file,
    clientId:
      globalThis.crypto?.randomUUID?.() ||
      `${Date.now()}-${Math.random().toString(36).slice(2)}-${file.name.length}`,
  }));
  for (const item of tagged) {
    const last = chunks[chunks.length - 1];
    const bytes = item.file.size || 0;
    const chunkBytes = last ? last.reduce((sum, i) => sum + (i.file.size || 0), 0) : 0;
    if (!last || last.length >= CHUNK_SIZE || (last.length && chunkBytes + bytes > MAX_CHUNK_BYTES)) {
      chunks.push([item]);
    } else {
      last.push(item);
    }
  }
  let uploaded = 0;
  const errors = [];
  const duplicates = [];

  for (let index = 0; index < chunks.length; index += 1) {
    const chunk = chunks[index];
    let lastError = null;
    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {      const form = new FormData();
      chunk.forEach((item) => form.append("files", item.file));
      form.append("client_ids", chunk.map((item) => item.clientId).join(","));
      form.append("event_id", eventId);
      form.append("album_id", albumId);
      form.append("watermark", String(watermark));
      form.append("download_enabled", String(downloadEnabled));
      form.append("published", String(published));
      form.append("allow_similar", String(allowSimilar));
      if (photographer) form.append("photographer", photographer);
      try {
        const { data } = await api.post("/admin/media/upload", form, {
          signal,
          onUploadProgress: (e) => {
            const inChunk = e.loaded / (e.total || 1);
            onProgress?.(Math.round(((index + inChunk) / chunks.length) * 100));
          },
        });
        uploaded += data.uploaded + (data.skipped || 0);
        (data.errors || []).forEach((err) => errors.push(err));
        (data.duplicates || []).forEach((d) => duplicates.push(d));
        lastError = null;
        break;
      } catch (err) {
        if (err.name === "CanceledError") throw err;
        const status = err.response?.status;
        lastError = err;
        const retryable = !status || status >= 500;
        if (!retryable || attempt === MAX_ATTEMPTS) break;
        await sleep(1500 * attempt);
      }
    }
    if (lastError) {
      const status = lastError.response?.status;
      if (chunk.length > 1 && (status === 413 || status >= 500 || !status)) {
        // Too heavy for one request: split and retry these files one by one.
        chunks.splice(index + 1, 0, ...chunk.map((item) => [item]));
        continue;
      }
      const detail = lastError.response?.data?.detail;
      chunk.forEach((item) =>
        errors.push({
          filename: item.file.name,
          error: typeof detail === "string" ? detail : "No se pudo subir",
        })
      );
    }
  }
  onProgress?.(100);
  return { uploaded, errors, duplicates };
}
